import { UniQChatMessage } from "us.common/interfaces";

/**
 * Builds a case summary chat message from AI-generated summary data.
 * @param caseNo The case number
 * @param aiGeneratedSummary Array of summary strings
 * @returns A UniQChatMessage with formatted content
 */
export const buildCaseSummaryMessage = (
  caseNo: string | number,
  aiGeneratedSummary: string[],
  additionalMessages: string[],
): UniQChatMessage | null => {
  try {
    if (!aiGeneratedSummary || aiGeneratedSummary.length === 0) {
      return null;
    }
    return {
      id: `case-sum-${caseNo}-${Date.now()}`,
      responseType: "TEXT",
      responseContent: `**Overview: Case ${caseNo}**\n\n${aiGeneratedSummary
        .map((item) => `• ${item}`)
        .join("\n")}
      ${
        additionalMessages.length > 0
          ? `\n---\n\n**Instructions**
      ${additionalMessages.map((item) => `${item}`).join("\n")} `
          : ``
      }
      `,
      links: [],
      timestamp: new Date(),
      isUser: false,
    };
  } catch {
    return null;
  }
};

/**
 * Checks if a case summary message exists for the given case number.
 * @param messages Array of chat messages
 * @param caseNo The case number to search for
 * @returns Boolean indicating if a case summary exists
 */
export const hasCaseSummary = (
  messages: any[],
  caseNo: string
): boolean => {
  try {
    return messages.some((msg) => msg.id.includes(`case-sum-${caseNo}`))
  } catch {
    return true;
  }
};

/**
 * Helper function to group messages by date
 * @param messages Array of chat messages
 * @returns Array of grouped messages with date separators
 */
export const groupMessagesByDate = (messages: UniQChatMessage[]) => {
  const grouped: Array<{ type: "date" | "message"; data: any; key: string }> =
    [];
  let currentDate: string | null = null;

  messages.forEach((message, index) => {
    const messageDate = new Date(message.timestamp);
    const dateKey = messageDate.toDateString();

    // Add date separator if this is a new date
    if (currentDate !== dateKey) {
      currentDate = dateKey;
      grouped.push({
        type: "date",
        data: messageDate,
        key: `date-${dateKey}`,
      });
    }

    // Add the message
    grouped.push({
      type: "message",
      data: message,
      key: `message-${message.id}`,
    });
  });

  return grouped;
};
